<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/55 px-4 py-6 backdrop-blur-[3px] sm:px-6 sm:py-10"
    role="dialog"
    aria-modal="true"
    aria-labelledby="create-ws-title"
    @click.self="emit('close')"
  >
    <div
      class="relative my-auto flex w-full max-w-lg max-h-[min(92vh,1200px)] flex-col overflow-hidden rounded border border-gray-200/85 text-left shadow-2xl shadow-slate-900/15 ring-1 ring-black/[0.05] animate-scale-in dark:border-gray-600/55 dark:shadow-black/45 dark:ring-white/[0.06]"
      style="background-color: var(--bg-modal); color: var(--text-primary)"
      @click.stop
    >
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500"
        aria-hidden="true"
      />

      <header
        class="flex shrink-0 items-start gap-4 border-b px-6 pb-4 pt-5"
        style="border-color: var(--border-primary)"
      >
        <div
          class="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-gradient-to-br from-indigo-500/15 to-violet-600/25 text-indigo-600 dark:from-indigo-400/20 dark:to-violet-500/15 dark:text-indigo-400"
        >
          <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
            />
          </svg>
        </div>
        <div class="min-w-0 flex-1 pt-0.5">
          <h2 id="create-ws-title" class="text-lg font-semibold tracking-tight" style="color: var(--text-primary)">
            {{ permissionBlocked ? t('createWorkspace.permission.title') : t('createWorkspace.title') }}
          </h2>
          <p v-if="!permissionBlocked" class="mt-1 text-sm leading-relaxed" style="color: var(--text-muted)">
            {{ t('createWorkspace.subtitle') }}
          </p>
        </div>
        <button
          type="button"
          class="modal-close shrink-0 rounded-full p-2 transition-colors hover:bg-black/[0.06] dark:hover:bg-white/[0.08]"
          style="color: var(--text-muted)"
          :aria-label="t('common.close')"
          @click="emit('close')"
        >
          <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </header>

      <div class="min-h-0 flex-1 overflow-y-auto px-6 pb-6 pt-5">
        <div
          v-if="isCheckingLimit"
          class="flex flex-col items-center justify-center py-12"
          style="color: var(--text-muted)"
        >
          <svg class="mb-3 h-8 w-8 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.2" />
            <path
              d="M22 12a10 10 0 0 0-10-10"
              stroke="currentColor"
              stroke-width="3"
              stroke-linecap="round"
            />
          </svg>
          <p class="text-sm font-medium">{{ t('createWorkspace.checkingLimit') }}</p>
        </div>

        <div
          v-else-if="permissionBlocked"
          class="rounded border border-sky-400/45 bg-sky-50/95 p-5 text-sky-950 shadow-sm dark:border-sky-500/35 dark:bg-sky-950/45 dark:text-sky-50"
        >
          <p class="text-sm font-semibold leading-snug">
            {{ t('createWorkspace.permission.message') }}
          </p>

          <div class="mt-5 space-y-3">
            <a
              :href="COMMERCIAL_WHATSAPP_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 rounded border border-emerald-500/35 bg-white/70 px-4 py-3 text-sm font-medium text-emerald-800 transition hover:bg-white dark:border-emerald-400/30 dark:bg-emerald-950/30 dark:text-emerald-100 dark:hover:bg-emerald-950/50"
            >
              <svg class="h-5 w-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span>
                {{ t('createWorkspace.permission.whatsappAction', { phone: COMMERCIAL_WHATSAPP_DISPLAY }) }}
              </span>
            </a>

            <a
              :href="DISCORD_INVITE_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-3 rounded border border-indigo-500/35 bg-white/70 px-4 py-3 text-sm font-medium text-indigo-800 transition hover:bg-white dark:border-indigo-400/30 dark:bg-indigo-950/30 dark:text-indigo-100 dark:hover:bg-indigo-950/50"
            >
              <svg class="h-5 w-5 shrink-0" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20.317 4.37a19.791 19.791 0 00-4.885-1.515.074.074 0 00-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 00-5.487 0 12.64 12.64 0 00-.617-1.25.077.077 0 00-.079-.037A19.736 19.736 0 003.677 4.37a.07.07 0 00-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 00.031.057 19.9 19.9 0 005.993 3.03.078.078 0 00.084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 00-.041-.106 13.107 13.107 0 01-1.872-.892.077.077 0 01-.008-.128 10.2 10.2 0 00.372-.292.074.074 0 01.077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 01.078.01c.12.098.246.198.373.292a.077.077 0 01-.006.127 12.299 12.299 0 01-1.873.892.077.077 0 00-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 00.084.028 19.839 19.839 0 006.002-3.03.077.077 0 00.032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 00-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              <span>{{ t('createWorkspace.permission.discordAction') }}</span>
            </a>

            <RouterLink
              to="/suporte"
              class="flex items-center justify-center rounded border border-transparent px-4 py-2 text-sm font-medium text-sky-700 underline-offset-2 hover:underline dark:text-sky-300"
              @click="emit('close')"
            >
              {{ t('createWorkspace.permission.supportPage') }}
            </RouterLink>
          </div>

          <div class="mt-5">
            <button type="button" class="w-full btn-secondary !rounded py-2.5" @click="emit('close')">
              {{ t('common.close') }}
            </button>
          </div>
        </div>

        <div
          v-else-if="creationLimit.blocked"
          class="rounded border border-amber-400/50 bg-amber-50/95 p-4 text-amber-900 shadow-sm dark:border-amber-500/35 dark:bg-amber-950/50 dark:text-amber-100"
        >
          <p class="text-sm font-semibold leading-snug">
            <template v-if="creationLimit.isAdminLimit">
              {{ t('createWorkspace.limit.adminReached', { current: creationLimit.current, max: creationLimit.max }) }}
            </template>
            <template v-else>
              {{ t('createWorkspace.limit.reached', { current: creationLimit.current, max: creationLimit.maxLabel }) }}
            </template>
          </p>
          <p v-if="!creationLimit.isAdminLimit" class="mt-1.5 text-xs opacity-90 dark:text-amber-200/90">
            {{ t('createWorkspace.limit.upgradeHint') }}
          </p>
          <p v-else class="mt-1.5 text-xs opacity-90 dark:text-amber-200/90">
            {{ t('createWorkspace.limit.adminHint') }}
          </p>
          <div class="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:gap-3">
            <button type="button" class="flex-1 btn-secondary !rounded py-2.5" @click="emit('close')">
              {{ t('common.close') }}
            </button>
            <button
              v-if="!creationLimit.isAdminLimit"
              type="button"
              class="flex-1 btn-primary !rounded py-2.5"
              @click="goToUpgrade"
            >
              {{ t('createWorkspace.limit.upgrade') }}
            </button>
          </div>
        </div>

        <form v-else @submit.prevent="handleSubmit" class="space-y-5">
          <!-- form content unchanged -->
          <div>
            <label
              for="nome"
              class="mb-2 block text-xs font-semibold uppercase tracking-wide"
              style="color: var(--text-tertiary)"
            >
              {{ t('createWorkspace.fields.name') }} <span class="text-rose-500">*</span>
            </label>
            <input
              id="nome"
              v-model="form.nome"
              type="text"
              required
              class="input-field !rounded"
              :placeholder="t('createWorkspace.fields.namePlaceholder')"
              :disabled="workspaceStore.isLoading"
            />
          </div>

          <div>
            <label
              for="descricao"
              class="mb-2 block text-xs font-semibold uppercase tracking-wide"
              style="color: var(--text-tertiary)"
            >
              {{ t('createWorkspace.fields.description') }}
              <span class="font-normal normal-case opacity-80">{{ t('createWorkspace.fields.optional') }}</span>
            </label>
            <textarea
              id="descricao"
              v-model="form.descricao"
              rows="3"
              class="input-field resize-none !rounded"
              :placeholder="t('createWorkspace.fields.descriptionPlaceholder')"
              :disabled="workspaceStore.isLoading"
            />
          </div>

          <div>
            <label class="mb-3 block text-xs font-semibold uppercase tracking-wide" style="color: var(--text-tertiary)">
              {{ t('createWorkspace.fields.type') }}
            </label>
            <p class="mb-3 text-sm leading-relaxed" style="color: var(--text-muted)">
              {{ t('createWorkspace.fields.typeHint') }}
            </p>
            <div class="space-y-3">
              <label class="relative block cursor-pointer">
                <input
                  v-model="form.tipo"
                  type="radio"
                  value="TAREFAS"
                  class="sr-only"
                  :disabled="workspaceStore.isLoading"
                />
                <div
                  class="flex items-center gap-4 rounded border p-4 transition-all"
                  :class="
                    form.tipo === 'TAREFAS'
                      ? 'border-2 border-indigo-500 bg-indigo-500/[0.08] shadow-sm dark:border-indigo-400 dark:bg-indigo-500/10'
                      : 'border border-gray-200 bg-white/40 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-800/30 dark:hover:border-gray-500'
                  "
                >
                  <div
                    class="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-[#5865F2] text-white shadow-md shadow-indigo-500/25"
                  >
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                      />
                    </svg>
                  </div>
                  <div class="min-w-0">
                    <p class="text-base font-semibold text-gray-900 dark:text-gray-100">{{ t('createWorkspace.types.tasks.title') }}</p>
                    <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                      {{ t('createWorkspace.types.tasks.description') }}
                    </p>
                  </div>
                </div>
              </label>

              <label class="relative block cursor-pointer">
                <input
                  v-model="form.tipo"
                  type="radio"
                  value="CRM"
                  class="sr-only"
                  :disabled="workspaceStore.isLoading"
                />
                <div
                  class="flex items-center gap-4 rounded border p-4 transition-all"
                  :class="
                    form.tipo === 'CRM'
                      ? 'border-2 border-emerald-500 bg-emerald-500/[0.08] shadow-sm dark:border-emerald-400 dark:bg-emerald-500/10'
                      : 'border border-gray-200 bg-white/40 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-800/30 dark:hover:border-gray-500'
                  "
                >
                  <div
                    class="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-emerald-500 text-white shadow-md shadow-emerald-500/25"
                  >
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </svg>
                  </div>
                  <div class="min-w-0">
                    <p class="text-base font-semibold text-gray-900 dark:text-gray-100">{{ t('createWorkspace.types.crm.title') }}</p>
                    <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                      {{ t('createWorkspace.types.crm.description') }}
                    </p>
                  </div>
                </div>
              </label>

              <label class="relative block cursor-pointer">
                <input
                  v-model="form.tipo"
                  type="radio"
                  value="SERVICOS"
                  class="sr-only"
                  :disabled="workspaceStore.isLoading"
                />
                <div
                  class="flex items-center gap-4 rounded border p-4 transition-all"
                  :class="
                    form.tipo === 'SERVICOS'
                      ? 'border-2 border-sky-500 bg-sky-500/[0.08] shadow-sm dark:border-sky-400 dark:bg-sky-500/10'
                      : 'border border-gray-200 bg-white/40 hover:border-gray-300 dark:border-gray-600 dark:bg-gray-800/30 dark:hover:border-gray-500'
                  "
                >
                  <div
                    class="flex h-12 w-12 shrink-0 items-center justify-center rounded bg-sky-500 text-white shadow-md shadow-sky-500/25"
                  >
                    <svg class="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div class="min-w-0">
                    <p class="text-base font-semibold text-gray-900 dark:text-gray-100">{{ t('createWorkspace.types.servicos.title') }}</p>
                    <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{{ t('createWorkspace.types.servicos.description') }}</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <WorkspaceIconSelector v-model="workspaceVisual" :disabled="workspaceStore.isLoading" />

          <ColorSelector v-model="form.corWorkspace" color-context="workspace" :disabled="workspaceStore.isLoading" />

          <div
            class="flex flex-col-reverse gap-2 border-t pt-5 sm:flex-row sm:justify-end sm:gap-3"
            style="border-color: var(--border-primary)"
          >
            <button
              type="button"
              class="btn-secondary flex-1 !rounded py-2.5 sm:flex-initial sm:min-w-[7.5rem]"
              :disabled="workspaceStore.isLoading"
              @click="emit('close')"
            >
              {{ t('common.cancel') }}
            </button>
            <button
              type="submit"
              class="btn-primary flex-1 !rounded py-2.5 font-semibold shadow-lg shadow-indigo-500/20 transition-all hover:shadow-indigo-500/30 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-initial sm:min-w-[10rem]"
              :disabled="workspaceStore.isLoading || !isFormValid || creationLimit.blocked"
            >
              <span v-if="workspaceStore.isLoading" class="flex items-center justify-center gap-2">
                <svg class="h-4 w-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {{ t('createWorkspace.submit.creating') }}
              </span>
              <span v-else>{{ t('createWorkspace.submit.create') }}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed, ref, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Swal from 'sweetalert2'
import { useWorkspaceStore } from '../stores/workspace'
import { useAuthStore } from '../stores/auth'
import api from '../services/api'
import WorkspaceIconSelector from './WorkspaceIconSelector.vue'
import ColorSelector from './ColorSelector.vue'
import {
  COMMERCIAL_WHATSAPP_DISPLAY,
  COMMERCIAL_WHATSAPP_URL,
  DISCORD_INVITE_URL
} from '../config/supportContact'

const emit = defineEmits(['close', 'workspace-created'])
const workspaceStore = useWorkspaceStore()
const authStore = useAuthStore()
const router = useRouter()
const { t } = useI18n()

const form = reactive({
  nome: '',
  descricao: '',
  tipo: 'TAREFAS',
  corWorkspace: '#5865F2'
})

const workspaceVisual = ref({
  type: 'icon',
  icon: 'task',
  image: ''
})

const isFormValid = computed(() => {
  return form.nome.trim() && form.tipo
})

const creationLimit = ref({
  blocked: false,
  isAdminLimit: false,
  current: 0,
  max: -1,
  maxLabel: '',
  planName: ''
})
const permissionBlocked = ref(false)
const isCheckingLimit = ref(true)

const isWorkspaceLimitError = (message) => {
  const normalized = String(message || '').toLowerCase()
  return (
    normalized.includes('limite de workspaces') ||
    normalized.includes('limite de workspace') ||
    normalized.includes('workspace(s)') ||
    (normalized.includes('limite') && normalized.includes('workspace'))
  )
}

const isPermissionError = (message) => {
  const normalized = String(message || '').toLowerCase()
  return normalized.includes('não tem permissão') || normalized.includes('nao tem permissao')
}

watch(
  () => form.tipo,
  (tipo) => {
    const key = String(tipo || 'TAREFAS').toUpperCase()
    if (key === 'SERVICOS') {
      form.descricao = t('createWorkspace.types.servicos.defaultDescription')
    } else if (key === 'CRM') {
      form.descricao = t('createWorkspace.types.crm.description')
    } else {
      form.descricao = t('createWorkspace.types.tasks.description')
    }
    loadCreationLimitStatus()
  }
)

const loadCreationLimitStatus = async () => {
  isCheckingLimit.value = true
  try {
    await workspaceStore.getWorkspaces()
    const limitesResp = await api.get('/planos/criacao-limites')
    const limites = limitesResp?.data || {}
    const isSuperAdmin = authStore.user?.role?.nome === 'SUPER_ADMIN'
    const podeCriar = limites.podeCriarWorkspace ?? authStore.user?.podeCriarWorkspace
    permissionBlocked.value = !isSuperAdmin && !Boolean(podeCriar)

    const tipo = String(form.tipo || 'TAREFAS').toUpperCase()
    const bucket = tipo === 'CRM' ? limites.crm : (tipo === 'SERVICOS' ? limites.servicos : limites.task)
    const total = limites.total || {}
    const adminBlocked = total.max != null && total.max >= 0 && Boolean(total.blocked)
    const planResp = await api.get('/planos/meu-plano')

    creationLimit.value = {
      blocked: adminBlocked || Boolean(bucket?.blocked),
      isAdminLimit: adminBlocked,
      current: adminBlocked ? Number(total.current ?? 0) : Number(bucket?.current ?? 0),
      max: adminBlocked ? Number(total.max ?? 0) : Number(bucket?.max ?? -1),
      maxLabel: adminBlocked ? String(total.max ?? '—') : (bucket?.maxLabel || '—'),
      planName: planResp?.data?.nome || ''
    }
  } catch (error) {
    console.warn('[CreateWorkspaceModal] Falha ao checar limite do plano:', error)
    const isSuperAdmin = authStore.user?.role?.nome === 'SUPER_ADMIN'
    permissionBlocked.value = !isSuperAdmin && !Boolean(authStore.user?.podeCriarWorkspace)
    creationLimit.value = {
      blocked: false,
      isAdminLimit: false,
      current: 0,
      max: -1,
      maxLabel: t('createWorkspace.unlimited'),
      planName: ''
    }
  } finally {
    isCheckingLimit.value = false
  }
}

const goToUpgrade = async () => {
  emit('close')
  await router.push('/upgrade')
}

const handleSubmit = async () => {
  if (permissionBlocked.value) {
    return
  }

  if (creationLimit.value.blocked) {
    if (creationLimit.value.isAdminLimit) {
      await Swal.fire({
        title: t('createWorkspace.swal.adminLimitTitle'),
        text: t('createWorkspace.limit.adminReached', {
          current: creationLimit.value.current,
          max: creationLimit.value.max
        }),
        icon: 'warning',
        confirmButtonText: t('common.close')
      })
    } else {
      await goToUpgrade()
    }
    return
  }

  const workspaceData = {
    nome: form.nome,
    descricao: form.descricao,
    tipo: form.tipo,
    corWorkspace: form.corWorkspace,
    fotoWorkspace: workspaceVisual.value.type === 'upload' ? workspaceVisual.value.image : '',
    icone: workspaceVisual.value.type === 'icon' ? workspaceVisual.value.icon : 'task'
  }

  const result = await workspaceStore.createWorkspace(workspaceData)

  if (result.success) {
    emit('workspace-created', result.workspace)
    emit('close')
    return
  }

  if (isPermissionError(result.error)) {
    permissionBlocked.value = true
    return
  }

  if (isWorkspaceLimitError(result.error)) {
    const isAdmin = String(result.error || '').toLowerCase().includes('limite de workspaces atingido')
    if (isAdmin) {
      await Swal.fire({
        title: t('createWorkspace.swal.adminLimitTitle'),
        text: result.error,
        icon: 'warning',
        confirmButtonText: t('common.close')
      })
      await loadCreationLimitStatus()
      return
    }
    const upgradeResult = await Swal.fire({
      title: t('createWorkspace.swal.limitTitle'),
      text: t('createWorkspace.swal.limitText'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('createWorkspace.swal.seeUpgrade'),
      cancelButtonText: t('common.close'),
      confirmButtonColor: '#8b5cf6',
      cancelButtonColor: '#6b7280',
      background: 'var(--bg-modal)',
      color: 'var(--text-primary)'
    })

    if (upgradeResult.isConfirmed) {
      await goToUpgrade()
    }
  }
}

onMounted(() => {
  loadCreationLimitStatus()
})
</script>
